import { AnyAction, Dispatch } from 'redux'
import { camelize, snakeize } from 'casing'
import { AxiosError, AxiosResponse } from 'axios'
import {
  DefaultSuccesrOnSubmitData,
  LoadingTypes,
  RequestFailureMessage,
  errorLogger,
  getErrorMessages,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'
import {
  fetchSellerData,
  fetchSellerDataMicrosoft,
  patchSellersData,
} from '../../api/marketplace/index'
import { ListingTypeUpdationUnsuccessful } from '../../common/utils/messagesContants'
import type { FlyOutListingData } from '../../types/flyout'
import type { RootState } from '../../store/types'

export enum FlyOutSyncsActionTypes {
  SET_FLYOUT_LISTING_DATA = 'SET_FLYOUT_LISTING_DATA',
  SET_FLYOUT_LISTING_DATA_MICROSOFT = 'SET_FLYOUT_LISTING_DATA_MICROSOFT',
}

export const setListingData = (
  listingData: Record<string, unknown> | FlyOutListingData
) => ({
  type: FlyOutSyncsActionTypes.SET_FLYOUT_LISTING_DATA,
  payload: listingData,
})

export const getFlyoutListingData =
  ({
    partnerId,
    metadata = false,
  }: {
    partnerId: string
    metadata?: boolean
  }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO))
    try {
      const { data } = await fetchSellerData({
        partnerId,
        metadata: { metadata },
      })
      const camelizedData = camelize(data) as Record<string, unknown> & {
        type?: unknown
      }
      const transformedData = {
        ...camelizedData,
        ...(camelizedData.type ? { sellerType: camelizedData.type } : {}),
      }
      delete transformedData.type
      await dispatch(setListingData(transformedData))
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      errorLogger({ globalState: getState() })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO))
    }
  }

export const setListingDataMicrosoft = (
  listingDataMicrosoft: Record<string, unknown>
) => ({
  type: FlyOutSyncsActionTypes.SET_FLYOUT_LISTING_DATA_MICROSOFT,
  payload: listingDataMicrosoft,
})

export const getFlyoutListingDataMicrosoft =
  ({ accountId }: { accountId: string }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO_MICROSOFT))
    try {
      const { data } = await fetchSellerDataMicrosoft({
        accountId,
      })
      await dispatch(setListingDataMicrosoft(camelize(data)))
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO_MICROSOFT))
    }
  }

export const patchSellerData =
  (
    partnerId: string,
    sellerData: FlyOutListingData,
    cloudMarketplace = 'aws',
    operations = 'update_partner_details'
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { sellerType, ...restData } = sellerData
      const dataToSend = {
        ...restData,
        ...(sellerType ? { type: sellerType } : {}),
      }
      const { data } = await patchSellersData(
        partnerId,
        snakeize(dataToSend),
        cloudMarketplace,
        operations
      )
      const camelizedData = camelize(data) as {
        updatePartnerDetails?: { updated?: boolean }
      }
      if (camelizedData.updatePartnerDetails?.updated) {
        await dispatch(setListingData(sellerData))
        await dispatch(
          updateAppAlert({
            message: DefaultSuccesrOnSubmitData,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      } else {
        await dispatch(
          updateAppAlert({
            message: ListingTypeUpdationUnsuccessful,
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        await dispatch(
          getFlyoutListingData({
            partnerId,
            metadata: false,
          }) as unknown as AnyAction
        )
      }
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      errorLogger({ globalState: getState() })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
